import { app as firebaseApp } from 'firebase/app'
import React from 'react'
import WallColors from '../data/WallColors'
import {
  GridProduct,
  InterfaceProduct,
  MechProduct,
  ProductRule,
  SkinProduct,
} from '../types/productTypes'
import { CloudProducts } from '../types/CloudProducts'
import { getDenormalizedSkinGridProducts } from '../data/getDenormalizedSkinGridProducts'
import { sortByColor, sortByItemNo } from '../products/productSorting'
// eslint-disable-next-line no-restricted-imports
import { REACT_APP_PRODUCTS_ENVIRONMENT } from '../environment'
import { logDebug } from '../debug/debugLogging'
import { ProductDataShape } from '../ProductDataShape'
import { CloudProductsReturnType } from './CloudProductsReturnType'

export function useLegacyCloudProducts(app: firebaseApp.App): CloudProductsReturnType {
  const [loadingCount, setLoadingCount] = React.useState(1)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [error, setError] = React.useState<any | null>(null)
  const [data, setData] = React.useState<ProductDataShape>({
    interfaces: [],
    skins: [],
    mechs: [],
    grids: [],
    skinGridProducts: [],
    rules: [],
    constants: {
      WiserHub: '',
      WiserSwitchDefaultMech: '',
    },
    wallColorOptions: WallColors,
      // new Array(25).fill(null).map((_, i) => ({ name: `color ${i+1}`, color: `rgb(${Math.round(255/25*i)}, ${Math.round(255/25*i)}, ${Math.round(255/25*i)})` }))
    mechMap: { },
    interfaceMap: { },
  })

  React.useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  React.useEffect(() => {
    const db = app.firestore()

    const productDataCollection = db.collection('products').doc('products')

    setLoadingCount(it => it + 1)

    productDataCollection.collection('skins')
      .get()
      .then(result => {
        const skins = result.docs.map(it => it.data()) as SkinProduct[]

        setData(current => ({
          ...current,
          skins,
          skinGridProducts: current.grids.length > 0 ? getDenormalizedSkinGridProducts(skins, current.grids) : [],
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    setLoadingCount(it => it + 1)

    productDataCollection.collection('grids')
      .get()
      .then(result => {
        const grids = (result.docs.map(it => it.data()) as GridProduct[]).sort(sortByItemNo)

        setData(current => ({
          ...current,
          grids,
          skinGridProducts: current.skins.length > 0 ? getDenormalizedSkinGridProducts(current.skins, grids) : [],
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    setLoadingCount(it => it + 1)

    productDataCollection.collection('interfaces')
      .get()
      .then(result => {
        const interfaces = (result.docs.map(it => it.data()) as InterfaceProduct[]).sort(sortByColor)

        setData(current => ({
          ...current,
          interfaces,
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    setLoadingCount(it => it + 1)

    productDataCollection.collection('mechs')
      .get()
      .then(result => {
        setData(current => ({
          ...current,
          mechs: result.docs.map(it => it.data()) as MechProduct[],
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    setLoadingCount(it => it + 1)

    productDataCollection.collection('rules')
      .get()
      .then(result => {
        setData(current => ({
          ...current,
          rule: result.docs.map(it => it.data()) as ProductRule[],
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    // clear initial load state
    setLoadingCount(it => it - 1)

  }, [app])

  return [loadingCount !== 0, error, data]
}

export default function useCloudProducts(app: firebaseApp.App): CloudProductsReturnType {
  const [loadingCount, setLoadingCount] = React.useState(1)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [error, setError] = React.useState<any | null>(null)
  const [data, setData] = React.useState<ProductDataShape>({
    interfaces: [],
    skins: [],
    mechs: [],
    grids: [],
    skinGridProducts: [],
    rules: [],
    constants: {
      WiserHub: '',
      WiserSwitchDefaultMech: '',
    },
    wallColorOptions: WallColors,
    mechMap: { },
    interfaceMap: { },
  })

  React.useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  React.useEffect(() => {
    const db = app.firestore()

    const productsDocId = REACT_APP_PRODUCTS_ENVIRONMENT

    if (productsDocId) {
      logDebug('useCloudProducts', `loading products from products/${productsDocId}`)
    }

    const productDataCollection = db.collection('products').doc(productsDocId || 'products')

    setLoadingCount(it => it + 1)

    productDataCollection.get()
      .then(result => {
        const data = result.data() as CloudProducts

        if (!data) {
          throw Error('data not found')
        }

        const { skins, interfaces, grids, mechs, rules, constants } = data

        setData(current => ({
          wallColorOptions: current.wallColorOptions,
          constants,
          skins,
          grids,
          interfaces,
          mechs,
          rules,
          skinGridProducts: getDenormalizedSkinGridProducts(skins, grids),
          mechMap: mechs.reduce<Record<string, MechProduct>>((accum, curr) => {
            accum[curr.itemNo] = curr
            return accum
          }, { }),
          interfaceMap: interfaces.reduce<Record<string, InterfaceProduct>>((accum, curr) => {
            accum[curr.itemNo] = curr
            return accum
          }, { }),
        }))
      })
      .catch(setError)
      .finally(() => setLoadingCount(it => it - 1))

    // clear initial load state
    setLoadingCount(it => it - 1)

  }, [app])

  return [loadingCount !== 0, error, data]
}

